import toast from "react-hot-toast";
import { useMutation } from "@tanstack/react-query";
import moment from "moment";

import { createAxiosInstance } from "api/axiosConfig";
import {
  DOCTOR_DETAILS,
  PATIENT_CONTROLER_URL,
  POST_PATIENT_HEALTHIDENTIFIERS,
} from "api/Service";
import { useErrorHandler } from "utils/errorHandler";

export const usePatientMutation = ({
  tenantVal,
  patientId,
  additionalFormData,
  checkedConsentEmail,
  checkedConsentSms,
  checkedConsentPhone,
  onSuccess,
}) => {
  const errorHandler = useErrorHandler();

  return useMutation({
    meta: { skipGlobalError: true },
    mutationFn: async (formData) => {
      let doctorDetailsId;
      const postDataForDoctorDetails = {
        tenant: tenantVal,
        firstName: additionalFormData.gpName,
        surgery: additionalFormData.gpSurgery,
        telephone: additionalFormData.gpTelephoneNumber,
        healthMail: additionalFormData.gpHealthMail,
        address: {
          tenant: tenantVal,
          city: additionalFormData.gpCity,
          addressLine1: additionalFormData.gpAddressLine1,
          addressLine2: additionalFormData.gpAddressLine2,
          addressLine3: additionalFormData.gpAddressLine3,
          postCode: additionalFormData.gpPostCode,
        },
      };

      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(
          `${tenantVal}${DOCTOR_DETAILS}`,
          postDataForDoctorDetails
        );
        doctorDetailsId =
          response && response.data && response.data.id ? response.data.id : "";
      } catch (error) {
        errorHandler(error);
        throw error;
      }

      const postData = {
        title: formData.title,
        firstName: formData.firstName,
        surname: formData.surname,
        dateOfBirth: formData.dateOfBirth
          ? moment(formData.dateOfBirth, "YYYY-MM-DD").format("YYYY-MM-DD")
          : undefined,
        gender:
          formData.gender && formData.gender !== "Select"
            ? formData.gender
            : "",
        homePhone: formData.homePhone,
        mobilePhone: formData.mobilePhone,
        email: formData.email,
        nationalNumber: formData.nationalNumber,
        contactBySms: checkedConsentSms,
        contactByEmail: checkedConsentEmail,
        contactByPhone: checkedConsentPhone,
        nextOfKin: formData.nextOfKin,
        nextOfKinRelationship: formData.nextOfKinRelationship,
        nextOfKinPhone: formData.nextOfKinPhone,
        address: {
          id: additionalFormData.addressId,
          tenant: tenantVal,
          addressLine1: formData.addressLine1,
          addressLine2: formData.addressLine2,
          addressLine3: formData.addressLine3,
          city: formData.city,
          county: formData.county,
          country: formData.country,
          postCode: formData.postCode,
        },
        doctorDetails: {
          tenant: tenantVal,
          firstName: additionalFormData.gpName,
          surgery: additionalFormData.gpSurgery,
          telephone: additionalFormData.gpTelephoneNumber,
          healthMail: additionalFormData.gpHealthMail,
          address: {
            tenant: tenantVal,
            city: additionalFormData.gpCity,
            addressLine1: additionalFormData.gpAddressLine1,
            addressLine2: additionalFormData.gpAddressLine2,
            addressLine3: additionalFormData.gpAddressLine3,
            postCode: additionalFormData.gpPostCode,
          },
        },
        doctorDetailsId: formData.doctorDetailsId || doctorDetailsId,
        // The medical card number lives on the Patient entity, not on health
        // identifiers (which has no medical-card field), so it must go on the
        // /patient payload — the same field the edit flow saves. Previously it
        // was only sent to /patienthealthidentifiers as `medicalCardNo`, which
        // the backend silently ignores, so a new patient's card never saved
        // until the record was edited and re-saved.
        medicalCardNumber: formData.medicalCardNumber || undefined,
        gms: additionalFormData.gms || "",
        died: formData.died,
        dateOfDeath: formData.dateOfDeath
          ? moment(formData.dateOfDeath, "YYYY-MM-DD").format("YYYY-MM-DD")
          : undefined,
        ethnicityCode: formData.ethnicityCode || null,
        financialClass: formData.financialClass || null,
        mothersMaidenName: formData.mothersMaidenName || null,
        patientPreference: formData.patientPreference ?? null,
        // Normalised by PatientProfileForm: a contact id or an email, the other null.
        primaryGpContactId: formData.primaryGpContactId ?? null,
        primaryGpEmail: formData.primaryGpEmail ?? null,
      };

      if (patientId) postData.id = patientId;

      if (additionalFormData.addressId)
        postData.address.id = additionalFormData.addressId;

      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}${PATIENT_CONTROLER_URL}`,
        postData
      );

      const savedPatientId = response.data?.id || patientId;

      if (savedPatientId) {
        const healthIdentifiersData = {
          patientId: savedPatientId,
          privateInsuranceProvider:
            formData.privateInsuranceProvider || undefined,
          privateInsuranceNumber:
            additionalFormData.privateInsuranceNumber || undefined,
        };

        await axiosInstance.post(
          `${tenantVal}${POST_PATIENT_HEALTHIDENTIFIERS}`,
          healthIdentifiersData
        );
      }

      return { data: response.data, formData };
    },
    onSuccess: (result) => {
      setTimeout(() => {
        onSuccess?.({
          patientId: result.data?.id ?? null,
          formData: result.formData,
        });
      }, 500);
    },
    onError: (error) => {
      if (error?.response?.status === 409) {
        toast.error(
          "A patient with these details may already exist. Check PPSN and demographics."
        );
      }
      errorHandler(error);
    },
  });
};
